import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { QueryParameter } from '../../../interfaces/queryString';
import { LocationService } from '../../../services/location.service';
import { FilterSet, FilterConfig, FilterSetService, FilterGroup } from './../../../services/filter-set.service';
import { Subscription } from 'rxjs';
import { BbTableService, TableSort } from '../../../services/bb-table.service';
import { SapiService } from '../../../services/sapi.service';
import { OverlayService } from '../../../services/overlay.service';

export interface BbFilterPreset {
	id?: string;
	label: string;
	value?: string;
	filterSet?: FilterSet;
	params?: QueryParameter[];
}

export interface BbFilterConfiguration {
	search?: boolean;
	linkedToUrl?: boolean;
	presets?: BbFilterPreset[];
	filters?: FilterConfig[];
	allowDatasource?: boolean;
}

@Component({
	selector: 'bb-filters',
	templateUrl: './bb-filters.component.html',
	styleUrls: ['./bb-filters.component.scss']
})

export class BbFiltersComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() filterConfig: BbFilterConfiguration;
	@Input() currentPath: string;
	@Input() search = false;
	@Input() allowCustomPreset: boolean;
	@Input() theme: string;
	@Input() presetToggable = false;
	@Input() lightBackground = false;
	@Output() onFilterSetChanged = new EventEmitter<FilterSet>();

	editing = false;
	checkedPresets: string[] = [];
	addingFilter = false;

	private searchValue = '';
	private paramsChangedSubscription: Subscription;
	private currentFilterSubscription: Subscription;
	private currentFilter: string | FilterSet = 'all';

	// Used for the quickfilters queryParams to keep current sort
	private sortProperty: string;
	private sortDirection: string;
	private linkedToUrl: boolean;

	constructor(
		private locationService: LocationService,
		private filterSetService: FilterSetService,
		private bbTableService: BbTableService,
		private sapiService: SapiService,
		private overlayService: OverlayService,
		protected router: Router
	) {
		this.currentFilterSubscription = this.filterSetService.currentFilterObservable.subscribe((currentFilter: string | FilterSet) => {
			this.currentFilter = currentFilter;
		});

		this.bbTableService.rowsSortedObservable.subscribe((sort: TableSort) => {
			this.sortProperty = sort.sortProperty;
			this.sortDirection = sort.sortDirection;
		});
	}

	ngOnInit() {
		if (this.filterConfig) {
			if (typeof this.filterConfig.linkedToUrl === 'undefined') {
				this.linkedToUrl = true;
			}
			if (this.linkedToUrl) {
				this.searchValue = this.locationService.getParam('q') ? this.locationService.getParam('q').values.join(',') : '';
			}
			this.filterSetService.setFilterConfig(this.filterConfig.filters);
			this.sapiService.getUrl('/user/me/settings/presets').subscribe((storedItems) => {
				this.overlayService.setLoading(false);
				this.checkedPresets = storedItems;
			}, (err) => {
				this.overlayService.setLoading(false);
				if ((this.filterConfig as BbFilterConfiguration).presets) {
					this.checkedPresets = (this.filterConfig as BbFilterConfiguration).presets
						.filter(preset => preset && !preset.id)  // no id, so default preset
						.map(preset => preset && preset.value);
				}
			});
		}

		this.paramsChangedSubscription = this.locationService.paramsChangedObservable.subscribe(newQueryParameters => {
			this.applyLocationParams();
		});

	}

	ngAfterViewInit() {
		this.applyLocationParams();
	}

	ngOnDestroy() {
		this.paramsChangedSubscription.unsubscribe();
		this.currentFilterSubscription.unsubscribe();
	}

	applyLocationParams() {
		let newSearchValue = '';
		const qParam = this.locationService.getParam('q');

		if (qParam) {

			//  use in search box
			qParam.values.forEach(value => {
				const valueParts: string[] = value.split(':');
				newSearchValue = decodeURIComponent(valueParts[0] === '*' ? valueParts[1] : value);
			});
		}

		if (newSearchValue !== this.searchValue) {
			this.searchValue = newSearchValue;
		}
	}


	doSearch() {
		const newValues: string[] = this.getQueryParameter();

		if (this.linkedToUrl) {
			const qParam: string = this.locationService.getParam('q') ? this.locationService.getParam('q').values.join(',') : '';

			// Only search when there's a difference in value
			if (qParam === this.searchValue) {
				return;
			}
			this.locationService.setParam('q', newValues);
		}
		this.filterSetService.setQuery(newValues.join(','));
	}

	getQueryParameter(): string[] {
		let newValues: string[] = [];

		// add a new prop:foo or *:foo value
		if (this.searchValue.indexOf(':') > -1) {

			// remove current search for this property (if any)
			const propertyName = this.searchValue.split(':').shift();

			newValues = newValues.filter(value => value.indexOf(propertyName + ':') === -1);

			newValues.push(encodeURIComponent(this.searchValue));
		} else if (this.searchValue) {
			newValues.push(encodeURIComponent(this.searchValue));
		}

		return newValues;
	}


	setEditing(editing: boolean = true) {
		this.editing = editing;
	}

	isFilterActive(preset: BbFilterPreset) {
		if (preset.id && typeof this.currentFilter !== 'string') { // complex custom preset
			for (let i = 0; i < this.currentFilter.length; i++) {
				const current: FilterGroup = this.currentFilter[i];
				for (let j = 0; j < preset.filterSet.length; j++) {
					if (JSON.stringify(current.filters) === JSON.stringify(preset.filterSet[j].filters)) {
						return true;
					}
				}
			}
		} else if (preset.id && typeof this.currentFilter === 'string') {  // simple custom preset
			const presetValue: any = preset.filterSet[0].filters[0].value;
			if (Array.isArray(presetValue)) {
				return this.currentFilter === presetValue.join();
			} else {
				return this.currentFilter === presetValue;
			}
		} else {
			return this.currentFilter === preset.value; // default preset
		}
	}

	presetClicked(preset: BbFilterPreset) {
		if (preset.value === 'all') {
			this.currentFilter = preset.value;
		}

		if (this.filterSetService.isLinkedToUrl()) {
			if (this.isFilterActive(preset)) {
				this.filterSetService.setFilterSetFromString('');
			}
			this.router.navigate([this.currentPath],
				{ queryParams: { fs: this.filterSetService.stringify(preset.filterSet), sortProperty: this.sortProperty, sortDirection: this.sortDirection } }
			);
		} else {
			this.filterSetService.setFilterSetFromString(this.filterSetService.stringify(preset.filterSet));
		}
	}
}
